import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

/**
 * 챌린지 검토 결과 — 판정(통과·보완·거절)·문제·고칠 점, 그리고 **사용자가 골라서** 반영하는 보완(2026-10-06).
 *
 * 본인 LLM 검토(「AI 검토」)와 클라우드의 공용 LLM 판정이 같은 모양(pass · fix · reject)이라 이 패널 하나를 함께 쓴다.
 * 반영은 고른 것만 — 모델의 제안을 통째로 덮어쓰지 않는다(utils/challengeEdit.applyReviewFixes).
 */
function ChallengeReviewPanel({ result, title, onApply, onClose, applyLabel }) {
  const { t } = useTranslation();
  const fixes = (result && result.fixes) || {};
  const edits = (result && result.edits) || [];
  // 처음부터 전부 고른 상태 — 사용자가 빼고 싶은 것만 끈다. 결과가 바뀌면 부모가 key 로 다시 만든다(효과로 맞추면 첫 화면이 빈 선택이다)
  const [picked, setPicked] = useState(() => ({
    edits: edits.map((_, i) => i),
    removeQuizNos: [...(fixes.removeQuizNos || [])],
    level: !!fixes.level,
    kind: !!fixes.kind,
  }));
  if (!result) return null;

  const toggle = (k, v) => setPicked(p => ({ ...p, [k]: p[k].includes(v) ? p[k].filter(x => x !== v) : [...p[k], v] }));
  const hasFix = edits.length || (fixes.removeQuizNos || []).length || fixes.level || fixes.kind;
  const levelName = (c) => t(`challenge:edit.levels.${c}`);
  const kindName = (k) => t(`challenge:edit.kinds.${k}`);

  return (
    <section className={`chal-review is-${result.verdict}`}>
      <div className="chal-review-head">
        <strong>{title}</strong>
        <span className="chal-review-verdict">{t(`challenge:review.verdict.${result.verdict}`)}</span>
        <span className="chal-editor-spacer" />
        <button type="button" className="chal-editor-link" onClick={onClose}>{t('common:ui.close')}</button>
      </div>
      {result.summary && <p className="chal-review-summary">{result.summary}</p>}
      {(result.issues || []).length > 0 && (
        <ul className="chal-review-issues">
          {result.issues.map((x, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={i}>
              {x.quizNo ? <b>Q{x.quizNo} </b> : null}{x.problem}
              {x.suggestion && <div className="chal-review-suggest">→ {x.suggestion}</div>}
            </li>
          ))}
        </ul>
      )}
      {hasFix ? (
        <div className="chal-review-fixes">
          <div className="chal-review-label">{t('challenge:review.pickFixes')}</div>
          {(fixes.removeQuizNos || []).map(n => (
            <label key={`r${n}`}><input type="checkbox" checked={picked.removeQuizNos.includes(n)} onChange={() => toggle('removeQuizNos', n)} />
              {t('challenge:review.removeQuiz', { n })}</label>
          ))}
          {fixes.level && (
            <label><input type="checkbox" checked={picked.level} onChange={() => setPicked(p => ({ ...p, level: !p.level }))} />
              {t('challenge:review.changeLevel', { level: levelName(fixes.level) })}</label>
          )}
          {fixes.kind && (
            <label><input type="checkbox" checked={picked.kind} onChange={() => setPicked(p => ({ ...p, kind: !p.kind }))} />
              {t('challenge:review.changeKind', { kind: kindName(fixes.kind) })}</label>
          )}
          {edits.map((e, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <label key={`e${i}`} className="chal-review-edit">
              <input type="checkbox" checked={picked.edits.includes(i)} onChange={() => toggle('edits', i)} />
              <span>{t('challenge:review.editField', { n: e.quizNo, field: t(`challenge:review.fields.${e.field}`) })}
                <pre>{e.value}</pre></span>
            </label>
          ))}
          <button type="button" className="app-btn app-btn-primary" onClick={() => onApply(picked)}>
            {applyLabel || t('challenge:review.apply')}
          </button>
        </div>
      ) : null}
      {(result.rejected || []).length > 0 && (
        <div className="chal-editor-help">{t('challenge:review.dropped', { n: result.rejected.length })}</div>
      )}
    </section>
  );
}

ChallengeReviewPanel.propTypes = {
  result: PropTypes.shape({
    verdict: PropTypes.oneOf(['pass', 'fix', 'reject']),
    summary: PropTypes.string,
    issues: PropTypes.array,
    fixes: PropTypes.object,
    edits: PropTypes.array,
    rejected: PropTypes.array,
  }),
  title: PropTypes.string.isRequired,
  onApply: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  applyLabel: PropTypes.string,
};

export default ChallengeReviewPanel;
